import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, getDocs, getDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase'; // Asegúrate que la ruta sea correcta
import SubcategoriaCard from '../components/SubcategoriaCard';
import Modal from '../components/admin/Modal'; // Reutilizamos el modal de admin
import Carousel from '../components/Carousel'; // Importar el Carousel
import '../styles/SubcategoriasPage.css'; // Crearemos este archivo para estilos
import '../styles/admin/Modal.css'; // Corregir la ruta de importación
import '../components/Carousel.css'; // Corregir la ruta para el CSS del Carousel

function SubcategoriasPage() {
  const { categoriaId } = useParams(); // Obtiene el ID de la categoría de la URL
  const [subcategorias, setSubcategorias] = useState([]);
  const [categoriaNombre, setCategoriaNombre] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estado para el modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSubcategoria, setSelectedSubcategoria] = useState(null);

  useEffect(() => {
    const fetchSubcategoriasAndCategoria = async () => {
      if (!categoriaId) return;
      setLoading(true);
      setError(null);
      setCategoriaNombre('');
      setSubcategorias([]);

      try {
        // 1. Obtener nombre de la categoría
        const categoriaDocRef = doc(db, 'recomendaciones', categoriaId);
        const categoriaSnap = await getDoc(categoriaDocRef);
        if (categoriaSnap.exists()) {
            setCategoriaNombre(categoriaSnap.data().nombre);
        } else {
            console.warn("Categoría no encontrada:", categoriaId);
            setError('La categoría solicitada no existe.');
            setLoading(false);
            return; // Salir si la categoría no existe
        }
        
        // 2. Obtener subcategorías
        const subcategoriasCollection = collection(db, 'recomendaciones', categoriaId, 'subcategorias');
        const q = query(subcategoriasCollection, orderBy('orden', 'asc'), orderBy('nombre', 'asc'));
        const querySnapshot = await getDocs(q);
        const subcategoriasData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data() // Esto debería incluir el array 'imagenes'
        }));
        setSubcategorias(subcategoriasData);

      } catch (err) {
        console.error("Error fetching data:", err);
        setError('No se pudieron cargar las recomendaciones. Inténtalo de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubcategoriasAndCategoria();
  }, [categoriaId]);

  // Funciones para manejar el modal
  const openModal = (subcategoriaData) => {
    console.log('Datos de la subcategoría al abrir modal:', subcategoriaData);
    setSelectedSubcategoria(subcategoriaData);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSubcategoria(null);
  };

  return (
    <div className="subcategorias-page">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Inicio</Link></li>
          <li className="breadcrumb-item"><Link to="/recomendaciones">Recomendaciones</Link></li>
          <li className="breadcrumb-item active" aria-current="page">
             {categoriaNombre || (loading ? 'Cargando...' : 'Categoría')} 
          </li>
        </ol>
      </nav>

      <h1>{categoriaNombre || 'Recomendaciones'}</h1>

      {loading && <p>Cargando recomendaciones...</p>}
      {error && <p className="error-message">{error}</p>}

      {!loading && !error && (
        <div className="subcategorias-grid">
          {subcategorias.length > 0 ? (
            subcategorias.map(sub => (
              <SubcategoriaCard
                key={sub.id}
                data={sub} // Pasar todos los datos
                nombre={sub.nombre}
                // Usar la primera imagen del array 'imagenes' como thumbnail
                // O usar la antigua 'imagenUrl' si aún existe por compatibilidad
                // O un placeholder si no hay ninguna imagen
                imagenUrl={sub.imagenes && sub.imagenes.length > 0 ? sub.imagenes[0] : sub.imagenUrl || '/placeholder.png'} 
                onCardClick={openModal} // La tarjeta abre el modal con sus datos
              />
            ))
          ) : (
            <p>No hay recomendaciones específicas en esta categoría por el momento.</p>
          )}
        </div>
      )}

      {/* Renderizar el Modal con Carousel */} 
      <Modal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        title={selectedSubcategoria?.nombre}
      >
        {selectedSubcategoria && (
          <div className="subcategoria-modal-content">
            {/* Log para depurar las imágenes justo antes de pasarlas */}
            {console.log('selectedSubcategoria.imagenes dentro del modal:', selectedSubcategoria?.imagenes)}
            
            {/* Usar Carousel si hay imágenes, con fallback a imagenUrl si existe */}
            {(selectedSubcategoria.imagenes && selectedSubcategoria.imagenes.length > 0) ? (
              <Carousel imagenes={selectedSubcategoria.imagenes} />
            ) : selectedSubcategoria.imagenUrl ? (
              // Fallback para datos antiguos que solo tengan imagenUrl
              <img 
                src={selectedSubcategoria.imagenUrl} 
                alt={`Imagen de ${selectedSubcategoria.nombre}`} 
                className="modal-image"
              />
            ) : null /* No mostrar nada si no hay imágenes */}

            {/* Descripción (sin cambios) */}
            {selectedSubcategoria.descripcion && (
              <div 
                className="modal-description quill-content"
                dangerouslySetInnerHTML={{ __html: selectedSubcategoria.descripcion }}
              />
            )}
            
            {/* Enlaces sociales */}
            <div className="social-links">
              {selectedSubcategoria.ubicacion && (
                <a 
                  href={selectedSubcategoria.ubicacion}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link location-link"
                >
                  <i className="fas fa-map-marker-alt"></i>
                  <span>Ubicación</span>
                </a>
              )}
              
              {selectedSubcategoria.whatsapp && (
                <a 
                  href={`https://wa.me/${selectedSubcategoria.whatsapp.replace(/\D/g, '')}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link whatsapp-link"
                >
                  <i className="fab fa-whatsapp"></i>
                  <span>WhatsApp</span>
                </a>
              )}
              
              {selectedSubcategoria.instagram && (
                <a 
                  href={`https://instagram.com/${selectedSubcategoria.instagram.replace('@', '')}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link instagram-link"
                >
                  <i className="fab fa-instagram"></i>
                  <span>Instagram</span>
                </a>
              )}
              
              {selectedSubcategoria.web && (
                <a 
                  href={selectedSubcategoria.web.startsWith('http') ? selectedSubcategoria.web : `https://${selectedSubcategoria.web}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-link web-link"
                >
                  <i className="fas fa-globe"></i>
                  <span>Sitio Web</span>
                </a>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default SubcategoriasPage; 